import React from "react";
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Row,
    Col,
    Badge,
    Table,
    Progress,
    Card,
    CardBody,
    Spinner,
    Alert,
} from "reactstrap";
import { useRanks } from "../../Hooks/use-ranks";
import { API_BASE_URL } from "../../api/api";

const getRankColor = (rankName) => {
    const name = rankName
        ?.normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();

    switch (name) {
        case "master":
            return "#FFD700";
        case "esmeralda":
            return "#008000";
        case "constructor":
            return "#0000FF";
        case "diamante":
            return "#C0C0C0";
        case "aprendiz elite":
            return "#800080";
        case "diamante embajador":
            return "#00CED1";
        case "diamante embajador estrella":
        case "diamante embajador estrella 🌟":
            return "#FF1493";
        default:
            return "#8B949E";
    }
};

const getRankImage = (rankName) => {
    const name = rankName
        ?.normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim();

    switch (name) {
        case "aprendiz elite":
            return "/images/rank/01.png";
        case "constructor":
            return "/images/rank/02.png";
        case "master":
        case "máster":
            return "/images/rank/03.png";
        case "esmeralda":
            return "/images/rank/04.png";
        case "diamante":
            return "/images/rank/05.png";
        case "diamante embajador":
            return "/images/rank/06.png";
        case "diamante embajador estrella":
        case "diamante embajador estrella 🌟":
            return "/images/rank/07.png";
        default:
            return "/images/rank/01.png";
    }
};

export const UserRangoDistribuidorModal = ({ isOpen, toggle, user }) => {

    const {
        isLoading,
        error,
        currentUserPoints,
        pointsToNextRank,
        currentRank,
        nextPointsRank,
        currentRankIndex,
        ranks,
        myRanks,
        currentPointsRank,
        activeDirects,
    } = useRanks(user?.id);

    if (!user) return null;

    if (isLoading) {
        return (
            <Modal isOpen={isOpen} toggle={toggle} size="xl">
                <ModalHeader toggle={toggle}>
                    <h5 className="mb-0">RANGOS POR DISTRIBUIDOR</h5>
                </ModalHeader>
                <ModalBody>
                    <div className="text-center py-5">
                        <Spinner color="primary" />
                        <p className="mt-2">Cargando información de rangos...</p>
                    </div>
                </ModalBody>
            </Modal>
        );
    }

    if (error || !ranks?.length) {
        return (
            <Modal isOpen={isOpen} toggle={toggle} size="xl">
                <ModalHeader toggle={toggle}>
                    <h5 className="mb-0">RANGOS POR DISTRIBUIDOR</h5>
                </ModalHeader>
                <ModalBody>
                    <Alert color="danger">
                        No fue posible cargar los rangos del usuario.
                    </Alert>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggle}>
                        Cerrar
                    </Button>
                </ModalFooter>
            </Modal>
        );
    }

    const getInitials = (name) => {
        if (!name) return "??";
        return name
            .split(" ")
            .map((n) => n[0])
            .join("")
            .toUpperCase()
            .slice(0, 2);
    };

    /**
     * Rango ACTUAL: el que alcanzan los puntos que el usuario tiene hoy.
     *
     * Antes esta tarjeta leía el rango del último corte guardado, el mismo que
     * "Rango Máximo Alcanzado", así que las dos columnas mostraban siempre lo
     * mismo y el rango actual no reflejaba los puntos del mes en curso.
     */
    const currentRankName = currentPointsRank?.name || "Sin rango";

    /** Rango MÁXIMO: el más alto de los cortes históricos. */
    const maxRankName = currentRank?.user_data?.rank?.name || "Sin rango";
    const maxRankDate = currentRank?.period?.end || null;
    const maxRankPoints = currentRank?.user_data?.total_points ?? null;

    const nextRankName = nextPointsRank?.name || "";
    const nextRankPoints = nextPointsRank?.points || 0;

    // El progreso se mide dentro del tramo entre el rango actual y el
    // siguiente, no desde cero: con `puntos / puntosDelSiguiente` alguien recién
    // ascendido aparecía ya casi en el próximo rango.
    const currentRankPoints = currentPointsRank?.points ?? 0;
    const tramo = nextRankPoints - currentRankPoints;
    const progressToNextRank = nextPointsRank
        ? Math.min(100, Math.max(0, ((currentUserPoints - currentRankPoints) / (tramo || 1)) * 100))
        : 100;

    const puntosFaltantes = pointsToNextRank;

    /**
     * Las dos condiciones que definen un rango: puntos y directos activos.
     *
     * Antes había cuatro. "Puntos en Red" y "Puntos en Cada Pierna" leían
     * `network_points` y `leg_points`, campos que no existen en ninguna
     * respuesta, así que siempre mostraban 0 y salían incumplidas. "Directos
     * Activos" tenía el mismo problema (`active_directs`) y además exigía 10
     * en duro, sin relación con el rango.
     *
     * Se evalúa contra el rango que los PUNTOS alcanzan, no contra el del
     * histórico: la pregunta que responde este bloque es si el usuario cumple
     * hoy las condiciones de su rango.
     */
    const rankToValidate = currentPointsRank;

    const requiredPoints = rankToValidate?.points ?? 0;
    // required_active_users ya viene resuelto por el hook: si el rango no lo
    // define, los tres primeros piden 3 y de ahí en adelante el requisito es
    // la posición del rango.
    const requiredActiveDirects = rankToValidate?.required_active_users ?? 3;

    const condiciones = [
        {
            nombre: "Puntos",
            requerido: requiredPoints,
            actual: currentUserPoints,
            cumple: currentUserPoints >= requiredPoints
        },
        {
            nombre: "Directos Activos",
            requerido: requiredActiveDirects,
            actual: activeDirects,
            cumple: activeDirects >= requiredActiveDirects
        },
    ];

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="xl">
            <ModalHeader toggle={toggle}>
                <h5 className="mb-0">RANGOS POR DISTRIBUIDOR</h5>
            </ModalHeader>
            <ModalBody>
                <Card className="mb-4" style={{ border: "1px solid #e9ecef", borderRadius: "12px" }}>
                    <CardBody>
                        <Row>
                            <Col md="4" className="d-flex align-items-center">
                                <div
                                    className="me-3"
                                    style={{
                                        width: 80,
                                        height: 80,
                                    }}
                                >
                                    <img
                                        src={`${API_BASE_URL}/users/show/photo/${user?.photo || ""}`}
                                        alt={user?.name || "Usuario"}
                                        style={{
                                            width: "80px",
                                            height: "80px",
                                            borderRadius: "50%",
                                            objectFit: "cover",
                                            border: "2px solid #e9ecef",
                                        }}
                                    />
                                </div>
                                <div className="ms-3">
                                    <h5 className="fw-bold mb-0">{user?.name}</h5>
                                    <small className="text-muted">Código: {user?.multyhealth_code}</small>
                                    <div>
                                        <Badge color="success" className="mt-1">
                                            <i className="fa fa-circle me-1" style={{ fontSize: "6px" }} /> Activo
                                        </Badge>
                                    </div>
                                </div>
                            </Col>

                            <Col md="3" style={{ borderLeft: "1px solid #e9ecef", paddingLeft: "20px" }}>
                                <p className="text-muted small mb-0">Rango Actual</p>
                                <div className="d-flex align-items-center gap-2">
                                    <h3 className="fw-bold mb-0" style={{ color: getRankColor(currentRankName) }}>
                                        {currentRankName}
                                    </h3>
                                    <img
                                        src={getRankImage(currentRankName)}
                                        alt={currentRankName}
                                        style={{
                                            width: 30,
                                            height: 30,
                                            objectFit: "contain",
                                        }}
                                    />
                                </div>
                                <h5 className="text-muted mb-0">
                                    {currentUserPoints.toLocaleString()} puntos
                                </h5>
                                <small className="text-muted">Con los puntos del período actual</small>
                            </Col>

                            <Col md="3" style={{ borderLeft: "1px solid #e9ecef", paddingLeft: "20px" }}>
                                {nextPointsRank ? (
                                    <>
                                        <p className="text-muted small mb-0">Progreso hacia el siguiente rango</p>
                                        <div className="d-flex justify-content-between">
                                            <span className="fw-bold">Faltan {puntosFaltantes.toLocaleString()} puntos para {nextRankName}</span>
                                        </div>
                                        <Progress value={progressToNextRank} color="primary" className="mt-1" style={{ height: "8px" }} />
                                        <small className="text-muted">{progressToNextRank.toFixed(2)}%</small>
                                    </>
                                ) : (
                                    <>
                                        <p className="text-muted small mb-0">Progreso hacia el siguiente rango</p>
                                        <div className="d-flex justify-content-between">
                                            <span className="fw-bold">¡Rango máximo alcanzado!</span>
                                        </div>
                                        <Progress value={100} color="success" className="mt-1" style={{ height: "8px" }} />
                                        <small className="text-muted">100%</small>
                                    </>
                                )}
                            </Col>

                            <Col md="2" style={{ borderLeft: "1px solid #e9ecef", paddingLeft: "20px" }}>
                                <p className="text-muted small mb-0">Rango Máximo Alcanzado</p>
                                <div className="d-flex align-items-center gap-2">
                                    <h3 className="fw-bold mb-0" style={{ color: getRankColor(maxRankName) }}>
                                        {maxRankName}
                                    </h3>
                                    <img
                                        src={getRankImage(maxRankName)}
                                        alt={maxRankName}
                                        style={{ width: 30, height: 30, objectFit: "contain" }}
                                    />
                                </div>
                                {maxRankPoints !== null && (
                                    <div className="text-muted small">
                                        {maxRankPoints.toLocaleString()} puntos
                                    </div>
                                )}
                                <small className="text-muted">
                                    {maxRankDate ? new Date(maxRankDate).toLocaleDateString() : "No disponible"}
                                </small>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>

                <Row>
                    <Col md="6">
                        <h6 className="fw-bold mb-3">Validación automática de condiciones / reglas de rangos</h6>
                        <Table bordered className="text-center">
                            <thead className="table-light">
                                <tr>
                                    <th className="text-start">Condición</th>
                                    <th>Requerido</th>
                                    <th>Actual</th>
                                    <th>Cumple</th>
                                </tr>
                            </thead>
                            <tbody>
                                {condiciones.map((cond, index) => (
                                    <tr key={index}>
                                        <td className="text-start">{cond.nombre}</td>
                                        <td>{cond.requerido.toLocaleString()}</td>
                                        <td>{cond.actual.toLocaleString()}</td>
                                        <td>
                                            {cond.cumple ? (
                                                <span className="text-success">✔</span>
                                            ) : (
                                                <span className="text-danger">✘</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Col>
                    <Col md="6">
                        <h6 className="fw-bold mb-3">Tabla de Rangos</h6>
                        <Table bordered className="text-center">
                            <thead className="table-light">
                                <tr>
                                    <th>Rangos</th>
                                    <th>Puntos Requeridos</th>
                                    <th>Icono</th>
                                </tr>
                            </thead>
                            <tbody>
                                {ranks.map((rank, index) => {
                                    const isCurrent = rank.name === currentRankName;
                                    const isMax = rank.name === maxRankName;
                                    const isCompleted = rank.myRank;
                                    const rankColor = getRankColor(rank.name);
                                    return (
                                        <tr key={rank.id} className={isCurrent ? "table-primary" : ""}>
                                            <td>
                                                <div className="d-flex align-items-center gap-2">
                                                    <img
                                                        src={getRankImage(rank.name)}
                                                        alt={rank.name}
                                                        style={{
                                                            width: 24,
                                                            height: 24,
                                                            objectFit: "contain",
                                                        }}
                                                    />
                                                    <span style={{ color: isCurrent ? rankColor : "inherit" }}>
                                                        {rank.name}
                                                    </span>
                                                    {isCurrent && (
                                                        <Badge color="primary" className="ms-1" pill>
                                                            Actual
                                                        </Badge>
                                                    )}
                                                    {isMax && (
                                                        <Badge color="warning" className="ms-1" pill>
                                                            Máximo
                                                        </Badge>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                {index === 0 
                                                    ? `0 - ${rank.points.toLocaleString()}`
                                                    : index === ranks.length - 1
                                                    ? `${rank.points.toLocaleString()} - +`
                                                    : `${(ranks[index - 1]?.points || 0).toLocaleString()} - ${rank.points.toLocaleString()}`}
                                            </td>
                                            <td>
                                                {isCompleted || isCurrent ? "🏅" : "✘"}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </Col>
                </Row>

                <div className="bg-warning bg-opacity-10 p-3 rounded border border-warning mt-4">
                    <h6 className="fw-bold text-white mb-1">
                        <i className="fa fa-exclamation-triangle me-2" /> Importante
                    </h6>
                    <p className="mb-0 small">
                        Los rangos se evalúan antes del corte mensual y están sujetos a validación automática de todas las condiciones establecidas.
                    </p>
                </div>
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={toggle}>
                    Cerrar
                </Button>
            </ModalFooter>
        </Modal>
    );
};